/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

// Store
import useStore from '@store/index';
import { useElementSize } from '@mantine/hooks';
import {
	TApiResponseOrganizationPopulated,
	TParsedOrganization,
} from '../types';
import {
	TApiError,
	TOrganization,
} from '@global-types/api/api';
import {
	TParams,
	TSelectOption,
	TSortStatus,
} from '@global-types/app/app';
import { UseMutationResult } from '@tanstack/react-query';
import {
	cloneDeep,
	removeEmptyArrays,
} from '@utils/functions';
import { TMeasurabilityProjectQueryParams } from '@features/catalogs/meausrability-projects/list/types';
import { TQueryParams } from '@global-types/index';
import { useAuthorization } from '@lib/authorization';
import { ACLS } from '@config/constants/acls';
import { ROLES } from '@config/constants/roles';

/* -------------------------------------------------------------------------- */
/*                                  Hook                                      */
/* -------------------------------------------------------------------------- */
export const useOrganizations = () => {
	/**************************** Refs ****************************/
	const containerRef = useRef<HTMLDivElement | null>(
		null
	);

	/**************************** States ****************************/
	const [
		resetFilterOrganizations,
		setResetFilterOrganizations,
	] = useState(false);
	const [openDrawer, setOpenDrawer] = useState(false);
	const [
		openDrawerAddOrganization,
		setOpenDrawerAddOrganization,
	] = useState(false);

	const [
		openDrawerEditOrganization,
		setOpenDrawerEditOrganization,
	] = useState(false);

	const [showScrollFilters, setShowScrollFilters] =
		useState(false);
	const [
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
	] = useState(false);

	const [isOverflowFilters] = useState(false);

	const {
		organizationsPage,
		organizationsPageSize,
		setOrganizationsPage,
		setOrganizationsSelectedRecords,
		setOrganizationsOpenConfirmationModalDelete,
		setOrganizationsQueryParams,
		organizationsSortStatus,
		setOrganizationsSortStatus,
		setOrganizationsSortParams,
		organizationsSortParams,
		setOrganizationsPageSize,
		setOrganizationsSelectedRow,
		organizationsLastAccessTokenCreated,
		setOrganizationsLastAccessTokenCreated,
		organizationsDatatableOptions,
		setOrganizationsDatatableOptions,
		organizationsQueryParams,
		organizationsSelectedRow,
		organizationsOpenConfirmationModalDelete,
		organizationsSelectedRecords,
	} = useStore();
	const { checkAcl, checkAccess } = useAuthorization();

	const { ref, width: widthContainer } = useElementSize();
	/**************************** Drawer show more production ****************************/
	const handleAddItem = () => {
		setOpenDrawerAddOrganization(true);
	};

	const handleDeleteMultipleItems = () => {
		setOpenSelectedItemsToDeleteModal(true);
	};

	/**************************** Reset ****************************/
	const handleReset = () => {
		setOrganizationsPageSize(10);
		setOrganizationsQueryParams({});
		setOrganizationsLastAccessTokenCreated(null);
		setOrganizationsPage(1);
		setOrganizationsSortParams('-createdAt');
		setOrganizationsSortStatus({});
	};

	useEffect(() => {
		return () => {
			handleReset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	/**************************** Actions ****************************/

	const handleCloseAddOrganizationDrawer = () => {
		setOpenDrawerAddOrganization(false);
	};

	const handleCloseEditOrganizationDrawer = () => {
		setOpenDrawerEditOrganization(false);
	};

	const handleCloseShowMoreOrganizationDrawer = () => {
		setOpenDrawer(false);
	};

	const handleEditOrganizationDrawer = (
		row: TParsedOrganization
	) => {
		handleEdit(row);
		setOpenDrawer(false);
	};

	const handleEdit = (row: any) => {
		setOrganizationsSelectedRow(row);
		setOpenDrawerEditOrganization(true);
	};

	const handleChangeObfuscateIPFS = (
		row: any,
		obfuscateIPFS: any
	) => {
		obfuscateIPFS.mutate({
			id: row.id,
			obfuscateIPFS: !row.obfuscateIPFS,
		});
	};

	const handleCountryFilter = (
		_countries: TSelectOption[]
	) => {
		setOrganizationsPage(1);

		const countries = _countries.map(
			(_country: TSelectOption) => _country.value
		);

		if (countries.length == 0) {
			const _offsetMesurabilityProjectsQueryParams =
				cloneDeep(organizationsQueryParams);
			delete _offsetMesurabilityProjectsQueryParams[
				'location.countryCode'
			];
			setOrganizationsQueryParams(
				_offsetMesurabilityProjectsQueryParams
			);
			return;
		}
		const queryParamsCountries = {
			'location.countryCode': {
				$in: countries,
			},
		};
		const filterParams: TMeasurabilityProjectQueryParams =
			removeEmptyArrays({
				...organizationsQueryParams,
				...queryParamsCountries,
			});
		setOrganizationsQueryParams(filterParams);
	};

	const handleScroll = (scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	};
	const handleSortParams = (_column: TSortStatus) => {
		let sortParams = _column.columnAccessor;

		switch (_column.columnAccessor) {
			case 'organization':
				sortParams = 'entity';
				break;
			case 'contactName':
				sortParams = 'contact.firstName';
				break;
			case 'contactEmail':
				sortParams = 'contact.email';
				break;
			case 'place':
				sortParams = 'location.place';
				break;
			case 'contactPhone':
				sortParams = 'contact.phone';
				break;
			case 'countryCode':
				sortParams = 'location.countryCode';
				break;
			default:
				sortParams;
				break;
		}

		if (_column.direction == 'asc') {
			setOrganizationsSortParams(sortParams);
		} else {
			setOrganizationsSortParams('-' + sortParams);
		}
	};

	/*************************************** Delete one Item  ***************************************/

	const handleConfirmDeleteItem = (
		remove: UseMutationResult<
			TApiResponseOrganizationPopulated,
			TApiError,
			string,
			unknown
		>
	) => {
		setOrganizationsOpenConfirmationModalDelete({
			open: false,
			params: {},
		});

		remove.mutate(
			organizationsOpenConfirmationModalDelete.params.id
		);
		setOpenDrawer(false);
		setOrganizationsSelectedRecords([]);
		setOrganizationsLastAccessTokenCreated(null);
	};

	const handleCloseConfirmationDeleteItem = () => {
		setOrganizationsOpenConfirmationModalDelete({
			open: false,
			params: {},
		});
	};

	const handleOpenConfirmationDeleteItem = () => {
		setOrganizationsOpenConfirmationModalDelete({
			open: true,
			params: organizationsSelectedRow,
		});
	};

	const handleapplystatusfilter = (status: any) => {
		setOrganizationsSelectedRecords([]);
		setOrganizationsPage(1);
		const queryParamsVerified = {
			verified: status.map(
				(_status: any) => _status.value
			),
		};
		setOrganizationsQueryParams({
			...organizationsQueryParams,
			...queryParamsVerified,
		});
	};

	const handleapplyipfsstatusfilter = (status: any) => {
		setOrganizationsSelectedRecords([]);
		setOrganizationsPage(1);
		if (
			status &&
			Array.isArray(status) &&
			status.length > 0
		) {
			const queryParamsVerified = {
				obfuscateIPFS: status[0].value,
			};
			setOrganizationsQueryParams({
				...organizationsQueryParams,
				...queryParamsVerified,
			});
		} else {
			const { obfuscateIPFS, ...rest } =
				organizationsQueryParams;
			setOrganizationsQueryParams({
				...rest,
			});
		}
	};

	/*************************************** Datatables  ***************************************/
	const handleViewMore = (row: TParsedOrganization) => {
		setOrganizationsSelectedRow(row);
		setOpenDrawer(true);
	};

	const handleRowClicked = (row: {
		record: TOrganization;
	}) => {
		if (
			checkAcl(ACLS.ORGANIZATIONS.READ) &&
			checkAccess({
				allowedRoles: [
					ROLES.ADMIN,
					ROLES.SUPERADMIN,
					ROLES.STAFF,
				],
			})
		) {
			setOrganizationsSelectedRow(row.record);
			setOpenDrawer(true);
		}
	};

	const handleSelectedRecordsChange = (
		selectedRecords: TOrganization
	) => {
		setOrganizationsSelectedRecords(selectedRecords);
	};

	const handleSortStatusChange = (data: TSortStatus) => {
		handleSortParams(data);
		setOrganizationsSortStatus(data);
	};

	/**************************** Pagination ****************************/

	const handlepaginationchangesize = function (
		_size: string | number
	) {
		setOrganizationsPageSize(_size);
		setOrganizationsPage(1);
	};

	const handlechangepagination = (data: number) => {
		setOrganizationsPage(data);
	};

	const handleResetSelectedItems = () => {
		setOrganizationsSelectedRecords([]);
	};

	const handleOrganizationFilter = (
		organizations: TQueryParams[]
	) => {
		setOrganizationsPage(1);
		const queryParamsProjects = {
			entity: organizations.map(
				(_organization: TParams) => _organization.value
			),
		};

		setOrganizationsQueryParams({
			...organizationsQueryParams,
			...queryParamsProjects,
		});
	};

	const handleTechnologyFilter = (
		_technologies: TSelectOption[]
	) => {
		setOrganizationsPage(1);
		const queryParamsProjects = {
			technology: _technologies.map(
				(_technology: TSelectOption) => _technology.value
			),
		};

		const filterParams: TMeasurabilityProjectQueryParams =
			removeEmptyArrays({
				...organizationsQueryParams,
				...queryParamsProjects,
			});
		setOrganizationsQueryParams(filterParams);
	};

	/********************************************************************************************/

	return {
		handleResetSelectedItems,
		handlepaginationchangesize,
		handlechangepagination,
		handleSortStatusChange,
		handleSelectedRecordsChange,
		handleRowClicked,
		handleViewMore,
		handleOpenConfirmationDeleteItem,
		handleCloseConfirmationDeleteItem,
		handleConfirmDeleteItem,
		openSelectedItemsToDeleteModal,
		setOpenSelectedItemsToDeleteModal,
		showScrollFilters,
		setShowScrollFilters,
		openDrawerAddOrganization,
		setOpenDrawerAddOrganization,
		openDrawer,
		setOpenDrawer,
		handleOrganizationFilter,
		handleScroll,
		handleSortParams,
		containerRef,
		handleAddItem,
		handleDeleteMultipleItems,
		handleReset,
		ref,
		widthContainer,
		isOverflowFilters,
		organizationsPage,
		organizationsPageSize,
		organizationsSelectedRecords,
		setOrganizationsSelectedRecords,
		organizationsOpenConfirmationModalDelete,
		setOrganizationsOpenConfirmationModalDelete,
		organizationsQueryParams,
		organizationsSortStatus,
		organizationsSortParams,
		organizationsSelectedRow,
		organizationsLastAccessTokenCreated,
		setOrganizationsLastAccessTokenCreated,
		organizationsDatatableOptions,
		setOrganizationsDatatableOptions,
		handleCountryFilter,
		resetFilterOrganizations,
		setResetFilterOrganizations,
		handleTechnologyFilter,
		handleapplystatusfilter,
		handleapplyipfsstatusfilter,
		handleChangeObfuscateIPFS,
		openDrawerEditOrganization,
		setOpenDrawerEditOrganization,
		handleEdit,
		handleCloseAddOrganizationDrawer,
		handleCloseEditOrganizationDrawer,
		handleCloseShowMoreOrganizationDrawer,
		handleEditOrganizationDrawer,
	};
};
